import { stepModelCreator, stepModelCreatorAppJSON } from "./step.model";
import { getRulesModel } from "./rules.model";
import type {
  ConditionGroupMap,
  ConditionMap
} from "../components/RulesUI/Constraints/constraints.types";
import { uuidWithProcessId } from "../utils/db.utils";
import type {
  Field,
  Process,
  RuleMap,
  Step,
  Swimlane
} from "@certa/studio-core";
import { RuleType } from "@certa/studio-core";
import type { ZodRawProcess } from "@certa/queries/schemas/studio/deployableJSON.schema";
import type { ZodSwimlaneAppJSON } from "@certa/queries/schemas/studio/appProcessJSON.schema";

/**
 * TODO:
 * - Handle unhandled props
 * - Handle Data inheritance migration from v1 to v2
 */

const constraintRuleTypeMapping: Record<string, RuleType> = {
  accessGroupConstraints: RuleType.StepGroupAccess
};

const constraintRawPropMapping = {
  accessGroupConstraints: "access_group_constraints"
} as const;

const swimlaneConstraintKeys = ["accessGroupConstraints"] as const;

export const swimlaneModelCreator = (
  stepgroups: ZodRawProcess["stepgroups"]["0"],
  { processId }: { processId: string }
) => {
  const {
    dynamic_group_names: dynamicGroupNames,
    my_tasks_groups: myTaskGroups,
    static_groups: staticGroups,
    name,
    tag,
    external_tag: externalTag,
    __cs_comment: __csComment,
    steps: rawSteps,
    ...restStepgroups
  } = stepgroups;
  const swimlane: Swimlane = {
    ...restStepgroups,
    id: uuidWithProcessId(processId),
    dynamicGroupNames,
    myTaskGroups,
    staticGroups,
    name,
    tag,
    externalTag,

    accessGroupConstraints: [],
    steps: [],

    __cs_comment: __csComment,
    __createdAt: Date.now(),
    __modifiedAt: Date.now(),
    __tagReadonly: tag ? true : false
  };

  let aggregatedRuleMap: RuleMap = {};
  let aggregatedConditionGroupMap: ConditionGroupMap = {};
  let aggregatedConditionMap: ConditionMap = {};
  let aggregatedChildMappings: Process["process"]["childMappings"] = {};
  // Now we parse all constraints and populate them accordingly.
  swimlaneConstraintKeys.forEach(constraintKey => {
    // as we are doing ...restStepgroups, we need to delete the constraint from the swimlane
    // that we have added to the swimlane to avoid duplication
    if (constraintRawPropMapping[constraintKey] in swimlane) {
      delete swimlane[constraintRawPropMapping[constraintKey]];
    }
    if (!stepgroups[constraintRawPropMapping[constraintKey]]) {
      return;
    }
    const { ruleMap, conditionGroupMap, conditionMap } = getRulesModel(
      stepgroups[constraintRawPropMapping[constraintKey]],
      constraintRuleTypeMapping[constraintKey],
      { processId, targetFieldTag: swimlane.tag }
    );

    aggregatedRuleMap = { ...aggregatedRuleMap, ...ruleMap };
    aggregatedConditionGroupMap = {
      ...aggregatedConditionGroupMap,
      ...conditionGroupMap
    };

    aggregatedConditionMap = { ...aggregatedConditionMap, ...conditionMap };

    swimlane[constraintKey] = Object.keys(ruleMap);
  });

  // getting true sequence of steps
  const rawStepsKVArray = Object.entries(rawSteps).sort(
    (a, b) => Number(a[0]) - Number(b[0])
  );

  let aggregatedFieldsMap: Record<string, Field> = {};

  const steps = rawStepsKVArray.map(stepKV => {
    const {
      step,
      fieldsMap,
      ruleMap,
      conditionGroupMap,
      conditionMap,
      childMappings
    } = stepModelCreator(stepKV[1], { processId });
    aggregatedFieldsMap = { ...aggregatedFieldsMap, ...fieldsMap };

    aggregatedRuleMap = { ...aggregatedRuleMap, ...ruleMap };
    aggregatedConditionGroupMap = {
      ...aggregatedConditionGroupMap,
      ...conditionGroupMap
    };
    aggregatedChildMappings = {
      ...aggregatedChildMappings,
      ...childMappings
    };
    aggregatedConditionMap = { ...aggregatedConditionMap, ...conditionMap };
    return step;
  });

  swimlane.steps = steps.map(step => step.id);

  const aggregatedStepMap: Record<string, Step> = steps.reduce(
    (agg, step) => ({ ...agg, [step.id]: step }),
    {}
  );

  return {
    swimlane,
    stepsMap: aggregatedStepMap,
    fieldsMap: aggregatedFieldsMap,
    ruleMap: aggregatedRuleMap,
    conditionGroupMap: aggregatedConditionGroupMap,
    conditionMap: aggregatedConditionMap,
    childMappings: aggregatedChildMappings
  };
};

export const swimlaneModelCreatorAppJSON = (
  stepgroups: ZodSwimlaneAppJSON,
  { processId }: { processId: string }
) => {
  const {
    dynamic_group_names: dynamicGroupNames,
    my_tasks_groups: myTaskGroups,
    static_groups: staticGroups,
    name,
    tag,
    external_tag: externalTag,
    __cs_comment: __csComment,
    steps: rawSteps,
    __studio_id: __studioId,
    __studio_created_at: __studioCreatedAt,
    __studio_modified_at: __studioModifiedAt,
    ...restStepgroups
  } = stepgroups;
  const swimlane: Swimlane = {
    ...restStepgroups,
    id: __studioId ?? uuidWithProcessId(processId),
    dynamicGroupNames,
    myTaskGroups,
    staticGroups,
    name,
    tag,
    externalTag,

    accessGroupConstraints: [],
    steps: [],

    __cs_comment: __csComment,
    __createdAt: __studioCreatedAt || Date.now(),
    __modifiedAt: __studioModifiedAt || Date.now(),
    __tagReadonly: tag ? true : false
  };

  let aggregatedChildMappings: Process["process"]["childMappings"] = {};

  let aggregatedFieldsMap: Record<string, Field> = {};

  const steps = rawSteps.map(step => {
    const {
      step: stepRaw,
      fieldsMap,
      childMappings
    } = stepModelCreatorAppJSON(step, { processId });
    aggregatedFieldsMap = { ...aggregatedFieldsMap, ...fieldsMap };

    aggregatedChildMappings = {
      ...aggregatedChildMappings,
      ...childMappings
    };
    return stepRaw;
  });

  swimlane.steps = steps.map(step => step.id);

  const aggregatedStepMap: Record<string, Step> = steps.reduce(
    (agg, step) => ({ ...agg, [step.id]: step }),
    {}
  );

  swimlaneConstraintKeys.forEach(constraintKey => {
    // as we are doing ...restStepgroups, we need to delete the constraint from the swimlane
    // that we have added to the swimlane to avoid duplication
    if (constraintRawPropMapping[constraintKey] in swimlane) {
      delete swimlane[constraintRawPropMapping[constraintKey]];
    }
  });

  return {
    swimlane,
    stepsMap: aggregatedStepMap,
    fieldsMap: aggregatedFieldsMap,
    childMappings: aggregatedChildMappings
  };
};
